// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2025-11-18 22:03:24 +0100 (Tue, 18 Nov 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 158351 $
//  Purpose   : viewer options
// =============================================================================

// ---Framework imports
import {dict, mapItems, get, isObject, mergeDeep} from 'ldash'
import log from 'uix/logging'

// ---Globals
export const ZOOM_MODES = [
  'areaHeight',
  'areaWidth',
  'areaFit', // select width if area.width > area.height
  'layoutHeight',
  'layoutWidth',
  'layoutFit', // select width if layout.width > layout.height
]
export const WHEEL_MODES = ['zoom', 'panX', 'panY', 'none']

// XXX: obsolete: minZoom is now a calculated state
export const MIN_ZOOM = 10

const SimpleOptions = {
  layout: {
    dpiAware: true,
  },
  mouse: {
    wheelMode: null,
    wheelModeMeta: null,
    dragScroll: true,
  },
  viewport: {
    // important for center select
    virtual: 0.5,
  },
  zoom: {
    mode: 'layoutWidth',
  },
  quality: {
    devicePixelRatio: true,
    zoomLoadPlaceholder: 'none',
  },
  selection: {
    on: true,
    selectCenter: true,
    selectHover: false,
    selectClick: false,
  },
}

const SimpleHorizontalOptions1 = mergeDeep(SimpleOptions, {
  docs: `horizontal scrolling viewer
  * no zooming and panning
  * dpi-aware and highres
  `,
  layout: {
    align: 'horizontal',
    normalizedWidth: false, // all areas have the same height
    gutter_x: 0.1,
    gutter_y: 0.05,
  },
  mouse: {
    on: true,
    wheelMode: 'panX',
    wheelModeMeta: 'zoom',
  },
  zoom: {
    mode: 'layoutHeight',
  },
  resizing: {restoreInitialZoom: true},
})

const SimpleHorizontalOptions2 = mergeDeep(SimpleHorizontalOptions1, {
  layout: {
    align: 'horizontal.2.1',
  },
})

const SimpleVerticalOptions = mergeDeep(SimpleOptions, {
  docs: `vertical scrolling viewer
  * no zooming and panning
  * dpi-aware and highres
  `,
  layout: {
    //~ align: 'horizontal.2.1',
    align: 'vertical',
    //~ gutter_x: 0.05,
    gutter_y: 0.05,
  },
  mouse: {
    wheelMode: 'panY',
    wheelModeMeta: 'zoom',
  },
  zoom: {
    mode: 'layoutWidth',
  },
  viewport: {virtual: 0},
  resizing: {restoreInitialZoom: true},
})

const Vertical4 = mergeDeep(SimpleVerticalOptions, {
  docs: `vertical scrolling viewer`,
  layout: {
    align: 'vertical.4',
  },
})
const Vertical2 = mergeDeep(SimpleVerticalOptions, {
  docs: `vertical scrolling viewer`,
  layout: {
    align: 'vertical.2.1',
  },
})

const SimpleGridOptions = {
  docs: `manual zoomable square`,
  layout: {
    align: 'square',
    gutter_x: 0.05,
    gutter_y: 0.05,
    dpiAware: false,
    tiles: true,
  },
  mouse: {
    on: true,
    wheelMode: 'zoom',
    dragScroll: true,
  },
  zoom: {
    mode: 'areaHeight',
  },

  limit: 2000,
  selection: {
    on: true,
    selectCenter: true,
    selectHover: true,
    selectClick: false,
  },
  resizing: {restoreInitialZoom: false, restoreInitialPosition: false},
}

const SimpleTreeOptions = mergeDeep(SimpleGridOptions, {
  docs: `layout with TOC structure`,
  layout: {
    align: 'tree',
    normalizedWidth: false,
    gutter_x: 0.05,
    gutter_y: 0.05,
  },
  render: {
    paginations: 'fluid',
  },
  mouse: {
    wheelMode: 'panY',
    wheelModeMeta: 'zoom',
  },
  zoom: {
    mode: 'layoutHeight',
    max: 5000,
    min: 8, // there seems to be another 16 lower limit
  },
})

const ThumbOptions = {
  docs: `horizontal thumb stripe`,
  layout: {
    //~ dpiAware: true,
    dpiAware: false,
    normalizedWidth: false,
  },
  layout: {
    align: 'horizontal.2.1',
    gutter_x: 0.1,
    gutter_y: 0,
  },
  mouse: {
    on: true,
    wheelMode: 'panX',
    wheelModeMeta: null,
    dragScroll: {x: true, y: false},
  },
  zoom: {
    mode: 'layoutHeight',
    max: 15000,
  },

  selection: {
    on: true,
    center: false,
    hover: true,
  },
  quality: {
    devicePixelRatio: true,
  },
  viewport: {virtualX: 1, virtualY: 0},
}

const SingleOptions = {
  docs: `one page`,
  scrollTo: {bar: false},
  // is fast due to one page
  quality: {
    placeholder: 'none',
    devicePixelRatio: true,
  },
  resizing: {restoreInitialZoom: true},
}

export const OptionSets = {
  h1: SimpleHorizontalOptions1,
  h2: SimpleHorizontalOptions2,
  v1: SimpleVerticalOptions,
  v2: Vertical2,
  v4: Vertical4,
  tree: SimpleTreeOptions,
  grid: SimpleGridOptions,
  thumbs: ThumbOptions,
  single: SingleOptions,
}

export const validateOptions = options => {
  // patch some defaults
  // may really validate something later

  let {virtual, virtualX, virtualY} = options.viewport
  let opts = {viewport: {}}
  if (virtualX === null) {
    opts.viewport.virtualX = virtual
  }
  if (virtualY === null) {
    opts.viewport.virtualY = virtual
  }
  return mergeDeep(options, opts)
}

export const options = {
  __version__: 30,
  docs: `default options`,
  layout: {
    align: {
      docs: 'geometric alignment of items',
      default: 'square',
      options: [
        'square',
        'vertical',
        'horizontal',
        'tree',
        'vertical.2',
        'vertical.2.1',
        'vertical.4',
        'vertical.4.1',
        'horizontal.2',
        'horizontal.2.1',
        'horizontal.4',
        'horizontal.4.2',
      ],
    },
    normalizedWidth: {
      docs: 'all areas have the same width (true) or height (false)',
      default: true,
    },
    alignTransition: {
      docs: 'animate align transition in seconds',
      default: 0.0,
    },
    limit: {
      docs: 'limit number of items',
      default: 0,
    },
    spatialIndex: {
      docs: 'build a rtree index',
      default: true,
    },
    gutter_x: {
      docs: 'reserve space after page in x direction (1 is full page width)',
      default: 0.0,
    },
    gutter_y: {
      default: 0.0,
    },

    dpiAware: false,
    tiles: false,
  },

  render: {
    mode: {
      default: 'images',
      options: ['images', 'info'],
    },
    paginations: {
      default: 'off',
      // fluid tries to adapt to availble space
      // custom - uses `renderCustomPaginations({page, availableHeight, availableWidth})` property
      // XXX: static is not implemented
      options: ['off', 'fluid', 'custom'],
    },
    clsHover: null, // css class for selection/hover
    clsCenter: null, // css class for selection/center
  },

  viewport: {
    // negative virtual viewport, 0.5 is one half auf viewport height/width
    // no virtual space with 0.0
    virtual: 0.5,
    virtualX: null,
    virtualY: null,
    allowZoomInvalidate: false, // should minZoom calc allow invalid virtual space
  },

  resizing: {
    // do stuff on resize
    restoreInitialZoom: false, // should zoom be resetted to initially derived (zoom.mode) zoom
    restoreInitialPosition: {
      docs: 'modify center position to initial values (zoom.mode) on resize (true == both x/y)',
      default: false,
      options: [false, true, 'x', 'y'],
    },
  },
  zoom: {
    // should be called initialMode
    mode: {
      docs: 'derive initial zoom, viewer reset on external prop change',
      default: 'areaFit',
      options: [ZOOM_MODES, 'areaFit'],
    },
    // should zoomed area be aligned top/center (TODO: bottom, left, right?)
    initialAlign: {
      docs: 'initial viewport alignment/position',
      default: 'top',
      options: ['top', 'center'],
    },
    min: MIN_ZOOM, // is dynamic calculated in calcMinZoom
    max: 5000,
    control: {
      docs: 'render a zoom control in the top left corner',
      default: false,
      // true is a alias for button
      options: [false, true, 'button', 'slider', 'buttonSlider'],
    },
  },

  selection: {
    on: false,
    selectCenter: false,
    selectHover: false,
    selectClick: false,
    // should selectClick trigger if meta key pressed
    selectClickMeta: null, // or 'shift', 'alt' ...
    // select a layout subregion via rectangular selection
    // probably for zooming, but maybe other use-cases as well
    selectRect: true,
    // scroll+zoom to selected rectangle (switch to false if onRectSelect has control)
    selectRectZoom: true,
    // trigger rectangular selection with shift/alt/ctrl (use null for no meta key)
    selectRectKey: 'shift',
  },

  mouse: {
    docs: 'enable mouse support',
    on: true,
    // use {x: true, y: false} for custom axis targetting
    dragScroll: true,
    // trigger mouse drag with shift/alt/ctrl (use null for no meta key)
    dragScrollKey: null,
    // css class that can be used for customizing mouse cursor
    cursorClass: null,
    doubleClick: {
      docs: 'what to do on page area doubleclick',
      // scroll to area
      scroll: false,
      // increase zoom
      zoom: false,
    },
    wheelMode: {
      docs: 'use wheel for panning or zooming',
      default: 'zoom',
      options: [...WHEEL_MODES],
    },
    wheelModeMeta: {
      docs: 'like wheelmode but with pressed meta key (alt, shift)',
      default: 'panY',
      options: [...WHEEL_MODES],
    },
  },

  fps: 30, // for zoom + scroll modes

  quality: {
    adaptiveFps: false, // was only and experiment
    devicePixelRatio: false,
    // switched to default = none (breaks in pbox situation)
    placeholder: {
      docs: 'render placeholder during animations',
      default: 'none',
      options: ['none', 'thumb', 'autoWidth', 'grid'],
    },
    zoomLoadPlaceholder: {
      docs: 'we fetch new webcache widths during zoom, what to render during load',
      default: 'none',
      options: ['none', 'thumb'],
    },
  },

  scrollTo: {
    mode: {
      default: 'auto',
      options: ['zoom', 'scroll', 'jump', 'native', 'auto'],
    },
    bar: {
      docs: 'show scrollbars, (css overflow)',
      default: false,
    },
  },

  layers: {
    words: {minZoom: 500},
    search: {thumbZoom: 304},
    geometry: {minZoom: null, selectable: false, resizable: false},
  },
}

let getDefaults = opts => {
  if (isObject(opts)) {
    return dict(mapItems(opts, (name, value) => [name, get(value, 'default', value)]))
  } else {
    return opts
  }
}

export let defaultOptions = dict(mapItems(options, (name, opts) => [name, getDefaults(opts)]))
